import {
  formatNative,
  invertPrice,
  type ClosedPosition,
  type ExitPolicy,
  type PreviewAmount,
} from "@auto-pools/core";
import { useState, type ReactNode } from "react";
import {
  formatMoney,
  formatPercent,
  formatPrice,
  formatSignedAmount,
  formatSignedPercent,
  formatToken,
  shorten,
  signTone,
} from "../lib/format";
import { PriceScale } from "./PriceScale";
import { stopPolicyText } from "../lib/stopPolicy";
import { PositionPriceScrubber } from "./PositionPriceScrubber";
import { frameScale } from "../lib/rangeScale";
import { TokenPair, TokenPairName } from "./TokenPair";
import { DexBadge } from "./DexBadge";
import { dexName, dexPoolUrl } from "../lib/dex";
import { priceFrame, type PriceFrame } from "../lib/priceFrame";

// eslint-disable-next-line react-refresh/only-export-components -- History details reuse this formatter.
export function formatClosedWhen(value: string | null) {
  if (!value) return "Unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unavailable";
  return date.toLocaleString(undefined, { timeZoneName: "short" });
}

/**
 * The meta-row form of {@link formatClosedWhen}: short enough to sit beside the position id, and
 * still unambiguous. The year appears only when the timestamp is not from the current year, so a
 * recent position stays compact while an old one cannot be misread. The full timestamp, with its
 * timezone, stays available through the row's `title`.
 */
function compactWhen(value: string | null, now: Date) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Opening and closing times on the collapsed card; an unknown boundary is named, never guessed. */
function ClosedWhen({ item }: { item: ClosedPosition }) {
  const now = new Date();
  const opened = compactWhen(item.openedVerifiedAt, now),
    closed = compactWhen(item.closedVerifiedAt, now);
  if (!opened && !closed) return <span className="closed-when unknown">Dates unavailable</span>;
  return (
    <span
      className="closed-when"
      title={`Opened ${formatClosedWhen(item.openedVerifiedAt)} · Final exit ${formatClosedWhen(item.closedVerifiedAt)}`}
    >
      Opened {opened ?? "Unavailable"} → Final exit {closed ?? "Unavailable"}
    </span>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- History details reuse this label.
export function closedPositionLabel(item: ClosedPosition, frame: PriceFrame) {
  const reason = item.closeReason;
  return reason.origin === "manual"
    ? "Manual close"
    : reason.origin === "unknown"
      ? "Close cause unknown"
      : reason.rule === "out-of-range"
        ? "Automatic · out of range"
        : reason.rule === "low-yield"
          ? "Automatic · low fee yield"
          : `Automatic · ${reason.trigger ? `${frame.side(reason.trigger.side)} stop` : "policy"}`;
}

export function ClosedAmounts({
  values,
  item,
}: {
  values: PreviewAmount[] | null;
  item: Pick<ClosedPosition, "tokenA" | "tokenB">;
}) {
  return values === null ? (
    <>Unavailable</>
  ) : values.length === 0 ? (
    <>0</>
  ) : (
    <>
      {values.map((amount) => (
        <small key={amount.mint} title={`${amount.native} native units · ${amount.mint}`}>
          {formatToken(amount.decimal)} {tokenSymbol(item, amount.mint)} · {formatMoney(amount.usd)}
        </small>
      ))}
    </>
  );
}

const SOL_MINT = "So11111111111111111111111111111111111111112";

function tokenSymbol(item: Pick<ClosedPosition, "tokenA" | "tokenB">, mint: string) {
  return (
    [item.tokenA, item.tokenB].find((token) => token.mint === mint)?.symbol ??
    (mint === SOL_MINT ? "SOL" : shorten(mint, 6))
  );
}

function durationLabel(durationMs: number | null) {
  if (durationMs === null) return "Duration unavailable";
  const minutes = Math.max(0, Math.round(durationMs / 60_000));
  if (minutes === 0) return "Less than 1m";
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  const remainingMinutes = minutes % 60;
  return [
    days ? `${String(days)}d` : "",
    hours ? `${String(hours)}h` : "",
    remainingMinutes ? `${String(remainingMinutes)}m` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function settingSummary(item: ClosedPosition, settings: ExitPolicy | null) {
  if (!settings) return "— · Exit policy not recorded";
  const target = tokenSymbol(item, settings.targetMint);
  if (!settings.enabled) return `Automatic exit off · return to ${target}`;
  return `${stopPolicyText(settings)} · return to ${target}`;
}

/**
 * `executedRange` is always canonical (`history.ts:739` builds it as tokenA/tokenB), so every price
 * this returns is converted into `frame` before it leaves the function — the collapsed mini scale
 * and the no-`rangeMarks` expanded block (the only consumers without a server `RangeFrame`) get
 * display-frame prices straight from here, never a canonical value to convert themselves.
 */
function geometry(item: ClosedPosition, frame: PriceFrame) {
  const range = item.executedRange;
  if (!range) return null;
  const marker = item.closingObservation;
  const markerMatches = marker?.baseMint === range.baseMint && marker.quoteMint === range.quoteMint;
  const canonical = range.baseMint === item.tokenA.mint && range.quoteMint === item.tokenB.mint;
  const original = item.openingSetup;
  const originalMatches =
    original?.baseMint === range.baseMint && original.quoteMint === range.quoteMint;
  const stopPolicy =
    canonical && item.exitSettings
      ? item.exitSettings
      : !item.exitSettings && originalMatches
        ? original.initialExitPolicy
        : null;
  const usingOriginal = Boolean(stopPolicy) && stopPolicy === original?.initialExitPolicy;
  // `exitSettings` is canonical; `original.initialExitPolicy` is already mapped to the setup's own
  // frame (`original.inverted`) — each trigger set is converted from the frame it is actually in.
  const stops = stopPolicy?.enabled
    ? frame.stops(
        {
          lowerTrigger: stopPolicy.side !== "above" ? stopPolicy.lowerTrigger : undefined,
          upperTrigger: stopPolicy.side !== "below" ? stopPolicy.upperTrigger : undefined,
        },
        usingOriginal ? original.inverted : false,
      )
    : {};
  const bounds = frame.bounds(range.lowerPrice, range.upperPrice);
  return {
    min: bounds.lower,
    max: bounds.upper,
    marker: markerMatches ? frame.priceOrNull(marker.price) : null,
    entry: originalMatches ? (frame.priceOrNull(original.referencePrice) ?? undefined) : undefined,
    ...stops,
    stopSource: usingOriginal ? ("original" as const) : ("close" as const),
    markerMismatch: Boolean(marker && !markerMatches),
    unit: frame.unit,
  };
}

function displayMoney(value: string | null) {
  const formatted = formatMoney(value);
  return formatted === "Unavailable" ? "—" : formatted;
}

function displayPercent(value: string | null) {
  const formatted = formatPercent(value);
  return formatted === "Unavailable" ? "—" : formatted;
}

function CaptionIcon() {
  return (
    <svg viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="5.6" />
      <path d="M7 6.3v3.4" />
      <circle cx="7" cy="4.4" r="0.5" />
    </svg>
  );
}

function CompactResult({ item }: { item: ClosedPosition }) {
  const performance = item.performance;
  const complete = item.reportStatus?.status === "complete" && performance;
  if (!complete) {
    return (
      <section className="closed-result-cell">
        <small>Result</small>
        <b className="num unknown">—</b>
        <span className="closed-result-pending">report in progress</span>
      </section>
    );
  }
  const cash = performance.fundingCashResult;
  const symbol = cash?.fundingMint
    ? ([item.tokenA, item.tokenB].find((t) => t.mint === cash.fundingMint)?.symbol ??
      (cash.fundingMint === SOL_MINT ? "SOL" : cash.fundingMint))
    : "Funding";
  return (
    <section className="closed-result-cell">
      <small>Result</small>
      <div className="closed-result-rows">
        <div className={`closed-result-row cash-primary ${signTone(cash?.net)}`}>
          <span
            title={
              cash?.fundingMint
                ? ([item.tokenA, item.tokenB].find((t) => t.mint === cash.fundingMint)?.symbol ??
                  cash.fundingMint)
                : "Funding"
            }
          >
            {symbol}
          </span>
          <b className={signTone(cash?.net)}>{formatSignedPercent(cash?.percent)}</b>
          <em title={formatSignedAmount(cash?.net, symbol)}>
            {formatSignedAmount(cash?.net, symbol)}
          </em>
        </div>
        <div className={`closed-result-row cash-usd-secondary ${signTone(cash?.usd.net)}`}>
          <span>USD</span>
          <b className={signTone(cash?.usd.net)}>{formatSignedPercent(cash?.usd.percent)}</b>
          <em title={formatSignedAmount(cash?.usd.net)}>{formatSignedAmount(cash?.usd.net)}</em>
        </div>
      </div>
      {cash?.classification === "equivalent" && (
        <span className="closed-result-caption">Equivalent in original funding currency</span>
      )}
      {(cash?.reason ?? cash?.percentReason ?? (!cash ? "cash result unavailable" : null)) && (
        <span className="closed-result-caption">
          {cash?.reason ?? cash?.percentReason ?? "cash result unavailable"}
        </span>
      )}
      {cash?.usd.reason && <span className="closed-result-caption">USD · {cash.usd.reason}</span>}
      {item.retainedRefundableRent === null && (
        <span className="closed-result-caption closed-result-gap">
          <CaptionIcon />
          Retained deposit unattributed
        </span>
      )}
    </section>
  );
}

function lifecycleLabel(item: ClosedPosition, frame: PriceFrame) {
  if (item.closeReason.origin === "manual") return "Closed · manual";
  if (item.closeReason.origin === "automatic") {
    if (item.closeReason.rule === "out-of-range") return "Closed · automatic · out of range";
    if (item.closeReason.rule === "low-yield") return "Closed · automatic · low fee yield";
    const side = item.closeReason.trigger?.side;
    return side ? `Closed · automatic ${frame.side(side)}` : "Closed · automatic";
  }
  return "Closed";
}

const reportReasons: Record<string, string> = {
  PROVIDER_BUDGET_DEFERRED: "Historical prices are waiting for provider capacity.",
  HISTORICAL_EVIDENCE_UNAVAILABLE: "Some required historical evidence is unavailable.",
  WITHDRAWAL_MARKER_UNAVAILABLE: "The closing price marker is unavailable.",
  REPORT_FACT_CONFLICT: "Recorded historical evidence conflicts and needs review.",
};

function reportMessage(item: ClosedPosition) {
  const report = item.reportStatus;
  if (!report) return item.performance ? null : "Financial report pending.";
  const reason = report.reasonCode
    ? (reportReasons[report.reasonCode] ?? "Additional historical evidence is unavailable.")
    : null;
  if (report.status === "complete") {
    if (!item.performance)
      return "Financial report is marked complete but has no performance data.";
    // Keyed to the served signal rather than to any internal marker, so the caveat is present in
    // every case where the retained deposit is missing from the figures shown above.
    if (item.retainedRefundableRent === null)
      return "The retained refundable deposit could not be attributed, so it is excluded from the result shown.";
    return null;
  }
  const status = {
    pending: "Financial report pending.",
    running: "Financial report is being prepared.",
    partial: "Financial report is incomplete.",
    exhausted: "Financial report remains incomplete after all retry attempts.",
    conflict: "Financial report needs review because historical evidence conflicts.",
    unsupported: "Financial reporting is unavailable for this record.",
  }[report.status];
  return reason ? `${status} ${reason}` : status;
}

type ReportStatus = NonNullable<ClosedPosition["reportStatus"]>["status"];

const compactReportLabels: Record<ReportStatus, string | null> = {
  pending: "report in progress",
  running: "report in progress",
  partial: "report in progress",
  complete: null,
  exhausted: "report stopped · retries exhausted",
  conflict: "report stopped · needs review",
  unsupported: "report unavailable",
};

function compactUnavailableLabel(item: ClosedPosition, unavailable: string) {
  const status = item.reportStatus?.status;
  return (status && compactReportLabels[status]) ?? unavailable;
}

function nonZeroFlows(values: PreviewAmount[]) {
  const nonZero = values.filter((amount) => {
    try {
      return BigInt(amount.native) !== 0n;
    } catch {
      return true;
    }
  });
  return nonZero.length > 0 ? nonZero : values;
}

function signedTokenAmount(amount: PreviewAmount, item: ClosedPosition) {
  let prefix = "";
  try {
    if (BigInt(amount.native) > 0n) prefix = "+";
  } catch {
    // The server validates current responses; keep old malformed records visibly unmodified.
  }
  return `${prefix}${formatToken(amount.decimal)} ${tokenSymbol(item, amount.mint)}`;
}

function exactNativeAmount(value: string, mint: string, item: ClosedPosition) {
  // The SOL expense rows of a pool without SOL (`expenseNative`) still need the native scale.
  const token =
    [item.tokenA, item.tokenB].find((candidate) => candidate.mint === mint) ??
    (mint === SOL_MINT ? { symbol: "SOL", decimals: 9 } : null);
  if (!token) return "Unavailable";
  try {
    const decimal = formatNative(BigInt(value), token.decimals);
    const prefix = BigInt(value) > 0n ? "+" : "";
    return `${prefix}${formatToken(decimal)} ${token.symbol}`;
  } catch {
    return "Unavailable";
  }
}

function SupportingAmount({
  label,
  amount,
  item,
  note,
}: {
  label: string;
  amount: PreviewAmount | null;
  item: ClosedPosition;
  note?: string;
}) {
  return (
    <section>
      <small>{label}</small>
      <ClosedAmounts values={amount ? [amount] : null} item={item} />
      {note && <span>{note}</span>}
    </section>
  );
}

function FinancialResults({ item }: { item: ClosedPosition }) {
  const performance = item.performance;
  const message = reportMessage(item);
  const estimateNote = item.reportStatus?.historicalEstimate ? (
    <p className="closed-estimate-note">Some USD values use the nearest available minute price.</p>
  ) : null;
  if (!performance) {
    return (
      <>
        {estimateNote}
        <div className="closed-report-unavailable" role="status">
          <strong>{message ?? "Financial report pending."}</strong>
          <span>
            Cash results remain unavailable until the server supplies the performance report.
          </span>
        </div>
      </>
    );
  }
  const flows = nonZeroFlows(performance.netCashFlow);
  return (
    <>
      {estimateNote}
      {message && <p className="closed-report-note">{message}</p>}
      <div className="closed-financials" aria-label="Closed position financial results">
        <section>
          <small>Net cash spent</small>
          <b>{displayMoney(performance.cashSpentUsd)}</b>
          <span>Includes paid fees and account deposits</span>
        </section>
        <section>
          <small>Net cash returned</small>
          <b>{displayMoney(performance.cashReturnedAtOpeningPricesUsd)}</b>
          <span>At opening prices</span>
        </section>
        <section className="closed-net-result">
          <small>Cash result</small>
          <b className={signTone(performance.netCashPnlUsd)}>
            {displayMoney(performance.netCashPnlUsd)}
          </b>
          <span>
            At opening prices · {displayPercent(performance.periodReturnPercent)} actual period
          </span>
        </section>
        <section>
          <small>Annualized return (APR)</small>
          <b className={signTone(performance.simpleAnnualizedReturnPercent)}>
            {displayPercent(performance.simpleAnnualizedReturnPercent)}
          </b>
          <span>Illustrative simple annualization</span>
          {performance.shortPeriod && (
            <span>Short holding period; this figure can be misleading.</span>
          )}
        </section>
      </div>
      <div className="closed-financial-secondary" aria-label="Closing-price cash results">
        <section>
          <small>Actual cash returned</small>
          <b>{displayMoney(performance.actualCashReturnedUsd)}</b>
          <span>At closing marks</span>
        </section>
        <section>
          <small>Actual cash result</small>
          <b className={signTone(performance.actualCashPnlUsd)}>
            {displayMoney(performance.actualCashPnlUsd)}
          </b>
          <span>At closing marks</span>
        </section>
        <section>
          <small>Price movement effect</small>
          <b className={signTone(performance.currencyEffectUsd)}>
            {displayMoney(performance.currencyEffectUsd)}
          </b>
          <span>Change from opening prices to closing marks</span>
        </section>
        <section className="closed-native-result">
          <small>Native cash change</small>
          {flows.length === 0 ? (
            <b>0</b>
          ) : (
            flows.map((amount) => (
              <b
                key={amount.mint}
                className={signTone(amount.native)}
                title={`${amount.native} native units · ${amount.mint}`}
              >
                {signedTokenAmount(amount, item)}
              </b>
            ))
          )}
          {performance.singleCurrencyNative && (
            <span
              title={`Spent ${performance.singleCurrencyNative.spent}; returned ${performance.singleCurrencyNative.returned}; net ${performance.singleCurrencyNative.net} native units · ${performance.singleCurrencyNative.mint}`}
            >
              {expenseRows(item).length > 0
                ? `In ${tokenSymbol(item, performance.singleCurrencyNative.mint)}:`
                : "Same currency:"}{" "}
              {exactNativeAmount(
                performance.singleCurrencyNative.spent,
                performance.singleCurrencyNative.mint,
                item,
              ).replace(/^\+/, "")}{" "}
              spent →{" "}
              {exactNativeAmount(
                performance.singleCurrencyNative.returned,
                performance.singleCurrencyNative.mint,
                item,
              ).replace(/^\+/, "")}{" "}
              returned
            </span>
          )}
          {expenseRows(item).map((row) => (
            <span
              key={row.mint}
              className="closed-native-expense"
              title={`Spent ${row.spent}; returned ${row.returned}; net ${row.net} native units · ${row.mint}`}
            >
              {tokenSymbol(item, row.mint)} costs: {exactNativeAmount(row.net, row.mint, item)}
            </span>
          ))}
        </section>
      </div>
      <details className="closed-performance-formula">
        <summary>How calculated</summary>
        <div>
          <p>
            Fixed opening prices isolate the cash result from later currency price movement. Fees
            and rent already appear in the server ledger flows below.
          </p>
          <dl>
            <div>
              <dt>D · net opening ledger outflow</dt>
              <dd>
                {displayMoney(performance.cashSpentUsd)}
                <ClosedAmounts values={performance.openingCashFlow} item={item} />
              </dd>
            </div>
            <div>
              <dt>R · net closing cash flow at opening prices</dt>
              <dd>
                {displayMoney(performance.cashReturnedAtOpeningPricesUsd)}
                <ClosedAmounts values={performance.closingCashFlow} item={item} />
              </dd>
            </div>
            <div>
              <dt>N = R − D</dt>
              <dd className={signTone(performance.netCashPnlUsd)}>
                {displayMoney(performance.netCashPnlUsd)}
              </dd>
            </div>
            <div>
              <dt>r = N ÷ D × 100</dt>
              <dd className={signTone(performance.periodReturnPercent)}>
                {displayPercent(performance.periodReturnPercent)}
              </dd>
            </div>
            <div>
              <dt>Simple annualized return = r × 365 days ÷ duration</dt>
              <dd className={signTone(performance.simpleAnnualizedReturnPercent)}>
                {displayPercent(performance.simpleAnnualizedReturnPercent)} · illustrative, not
                compounded
              </dd>
            </div>
          </dl>
          <p>
            Actual returned cash and actual cash result use closing marks. Price movement effect is
            the difference attributable to changing currency prices.
          </p>
        </div>
      </details>
    </>
  );
}

function expenseRows(item: ClosedPosition) {
  return item.performance?.expenseNative ?? [];
}

function NativeResult({ item }: { item: ClosedPosition }) {
  const cash = item.performance?.fundingCashResult;
  const symbol = cash?.fundingMint ? tokenSymbol(item, cash.fundingMint) : "funding currency";
  return (
    <section className="closed-hero-native">
      <span className="position-hero-label">Net P&amp;L in {symbol}</span>
      <div className="closed-native-headline">
        <b className={signTone(cash?.net)}>
          {formatSignedPercent(cash?.percent)} · {formatSignedAmount(cash?.net, symbol)}
        </b>
      </div>
      <p className="closed-native-flow">
        {formatToken(cash?.spent ?? null)} → {formatToken(cash?.returned ?? null)} {symbol}
      </p>
      <small>Net opening cash spent includes paid expenses; unused reserves excluded.</small>
      {cash?.classification === "equivalent" && (
        <small>Equivalent in original funding currency</small>
      )}
      <small>
        {cash?.reason ?? cash?.percentReason ?? (!cash ? "cash result unavailable" : null)}
      </small>
      <small>Actual received assets (including unused reserves)</small>
      <ClosedAmounts values={cash?.receivedAssets ?? null} item={item} />
      {expenseRows(item).map((row) => (
        <p key={row.mint} className="closed-native-expense">
          Raw audit · {tokenSymbol(item, row.mint)} costs{" "}
          {exactNativeAmount(row.net, row.mint, item)}
        </p>
      ))}
    </section>
  );
}

function AprResult({ item }: { item: ClosedPosition }) {
  const performance = item.performance;
  const apr = performance?.simpleAnnualizedReturnPercent ?? null;
  return (
    <section>
      <span className="position-hero-label">APR</span>
      <b className={signTone(apr)}>{displayPercent(apr)}</b>
      {performance?.shortPeriod ? (
        <p className="closed-short-period">
          <span aria-hidden="true">ⓘ</span>
          Annualized from full cycle {durationLabel(item.durationMs)} — indicative
        </p>
      ) : apr === null ? (
        <small className="closed-unavailable-reason">Annualized return is not provided.</small>
      ) : (
        <p>Simple annualization over full cycle {durationLabel(item.durationMs)}</p>
      )}
    </section>
  );
}

function UsdResult({ item }: { item: ClosedPosition }) {
  const cash = item.performance?.fundingCashResult;
  const pnl = cash?.usd.net ?? null;
  const spent = cash?.usd.spent ?? null;
  const returned = cash?.usd.returned ?? null;
  const period = cash?.usd.percent ?? null;
  return (
    <section>
      <span className="position-hero-label">Cash USD result</span>
      <b className={signTone(pnl)}>{displayMoney(pnl)}</b>
      <p className="closed-usd-flow">
        <span>{displayMoney(spent)}</span>
        <span aria-hidden="true">→</span>
        <strong>{displayMoney(returned)}</strong>
      </p>
      <small className={period === null ? "closed-unavailable-reason" : undefined}>
        Boundary-price USD return: {displayPercent(period)}
      </small>
    </section>
  );
}

function ReportStatus({ item }: { item: ClosedPosition }) {
  const report = item.reportStatus;
  const message = reportMessage(item);
  if (!message) return null;
  const working = report?.status === "pending" || report?.status === "running";
  return (
    <div
      className={`closed-report-status ${report?.status ?? "pending"}`}
      role="status"
      data-report-status={report?.status ?? "pending"}
    >
      {working && <span className="spinner" aria-hidden="true" />}
      <div>
        <strong>{message}</strong>
        <span>
          {report
            ? `Status ${report.status} · ${String(report.attempts)} attempt${report.attempts === 1 ? "" : "s"}`
            : "Historical report has not been supplied."}
        </span>
      </div>
      <small>Only supplied values are shown; unavailable values remain —.</small>
    </div>
  );
}

function ResultComposition({ item }: { item: ClosedPosition }) {
  const native = item.performance?.singleCurrencyNative ?? null;
  const start = native
    ? exactNativeAmount(native.spent, native.mint, item).replace(/^\+/, "")
    : null;
  const final = native
    ? exactNativeAmount(native.returned, native.mint, item).replace(/^\+/, "")
    : null;
  const symbol = native ? tokenSymbol(item, native.mint) : null;
  return (
    <section className="closed-result-composition" aria-label="Closed result composition">
      <header>
        <h3>Where the result came from</h3>
        <span>{symbol ? `in ${symbol}` : "Native unit unavailable"}</span>
      </header>
      <dl>
        <div>
          <dt>Start</dt>
          <dd className={start && start !== "Unavailable" ? "" : "unknown"}>
            {start && start !== "Unavailable" ? start : "—"}
          </dd>
        </div>
        <div className="closed-composition-final">
          <dt>Final</dt>
          <dd className={final && final !== "Unavailable" ? "" : "unknown"}>
            {final && final !== "Unavailable" ? final : "—"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function ClosedRange({
  item,
  range,
  scrubPrice,
  pairFrame,
}: {
  item: ClosedPosition;
  range: ReturnType<typeof geometry>;
  /** In the position's own `executedRange.baseMint/quoteMint` orientation; converted below to
   * whichever orientation the currently-shown frame uses. */
  scrubPrice?: string | null;
  /** The card's rule frame — named `pairFrame` here so it is never confused with the RangeFrame
   * `frame` local below, which is one server-stated orientation, not the pair-level helper. */
  pairFrame: PriceFrame;
}) {
  const trigger = item.closeReason.origin === "automatic" ? item.closeReason.trigger : undefined;
  const outOfRange =
    item.closeReason.origin === "automatic" && item.closeReason.rule === "out-of-range";
  /** Opens in the RULE frame (R5); ⇄ still flips, but now means "flipped against the rule". */
  const [flipped, setFlipped] = useState(false);
  const orientations = item.rangeMarks ?? null;
  const frame = pairFrame.rangeFrame(orientations, flipped);
  const otherFrame = pairFrame.rangeFrame(orientations, !flipped);
  // `geometry` stays the authority on WHICH stops the closed bar draws — a disarmed or one-sided
  // policy draws none — while the frame supplies what each drawn mark is worth in this quote.
  // `geometry` names its stops in the card's rule frame; `frameScale` wants CANONICAL sides, so an
  // inverted rule frame hands its display-lower presence over as the canonical upper, and back.
  const scale =
    frame && range
      ? frameScale(
          frame,
          pairFrame.inverted
            ? { lower: range.upperStop !== undefined, upper: range.lowerStop !== undefined }
            : { lower: range.lowerStop !== undefined, upper: range.upperStop !== undefined },
        )
      : null;
  const unit = frame ? pairFrame.unitOf(frame.baseMint, frame.quoteMint) : pairFrame.unit;
  const executed = item.executedRange;
  // The scrub price always arrives canonical (it comes from the price-history series whose
  // base/quote = `executedRange` = canonical). With a server `RangeFrame` shown, reciprocate it
  // exactly the way that frame's own marks are reciprocated; without one (the `geometry()`
  // fallback, already in the rule frame) convert it the same way as every other fallback price.
  const frameScrubPrice =
    scrubPrice == null
      ? null
      : !frame
        ? pairFrame.priceOrNull(scrubPrice)
        : executed &&
            (frame.baseMint !== executed.baseMint || frame.quoteMint !== executed.quoteMint)
          ? invertPrice(scrubPrice)
          : scrubPrice;
  return (
    <section className="closed-range-section" aria-label="Executed range at close">
      <header>
        <h3>
          Range
          {frame && (
            <span className="range-requested">
              {" · set at open "}
              <span className="num">
                {formatSignedPercent(frame.requestedLowerPercent)} /{" "}
                {formatSignedPercent(frame.requestedUpperPercent)}
              </span>
            </span>
          )}
        </h3>
        <div className="position-range-heading-actions">
          <span>{unit}</span>
          {otherFrame && (
            <button
              type="button"
              className="button ghost small range-flip"
              aria-label={`Show the range as ${pairFrame.unitOf(otherFrame.baseMint, otherFrame.quoteMint)}`}
              onClick={() => {
                setFlipped((v) => !v);
              }}
            >
              ⇄
            </button>
          )}
        </div>
      </header>
      {range ? (
        <>
          <PriceScale
            compact
            closed
            min={scale?.min ?? range.min}
            max={scale?.max ?? range.max}
            current={scale ? scale.current : range.marker}
            entry={scale?.entry ?? range.entry}
            lowerStop={scale ? scale.lowerStop : range.lowerStop}
            upperStop={scale ? scale.upperStop : range.upperStop}
            percents={scale?.percents ?? null}
            scrubPrice={frameScrubPrice}
            unit={unit}
          />
          {range.marker !== null && (
            <small className="closed-range-note">
              {item.closingObservation?.quality === "historical-estimate"
                ? "Minute price estimate near withdrawal"
                : "Price observed near withdrawal"}
              ; not the final conversion execution price.
            </small>
          )}
          {range.stopSource === "original" &&
            (Boolean(range.lowerStop) || Boolean(range.upperStop)) && (
              <small className="closed-range-note">Original opening stops shown</small>
            )}
          {range.markerMismatch && (
            <small className="closed-range-note">
              Observed price mint orientation does not match this executed range.
            </small>
          )}
        </>
      ) : (
        <div className="price-scale unavailable closed-geometry-unavailable">
          <strong>—</strong>
          <small>Executed range geometry is not provided.</small>
        </div>
      )}
      {trigger && (
        <p className="closed-trigger-note">
          {outOfRange ? (
            <>
              Out of range · {pairFrame.side(trigger.side)} edge{" "}
              {formatPrice(pairFrame.price(trigger.threshold), "long")}
            </>
          ) : (
            <>
              Automatic {pairFrame.side(trigger.side)} stop triggered at{" "}
              {formatPrice(pairFrame.price(trigger.observedPrice), "long", [
                pairFrame.price(trigger.threshold),
              ])}
              ; saved threshold{" "}
              {formatPrice(pairFrame.price(trigger.threshold), "long", [
                pairFrame.price(trigger.observedPrice),
              ])}
              .
            </>
          )}
        </p>
      )}
    </section>
  );
}

function ClosedFooter({ item, frame }: { item: ClosedPosition; frame: PriceFrame }) {
  const setup = item.openingSetup;
  const shownExit = item.exitSettings
    ? frame.policy(item.exitSettings)
    : setup
      ? frame.fromSetup(setup).initialExitPolicy
      : null;
  const quality = item.closingObservation?.quality;
  return (
    <footer className="closed-position-footer">
      <section>
        <span>Time in LP</span>
        <b>{durationLabel(item.lpDurationMs ?? null)}</b>
      </section>
      <section>
        <span>LP ended → Final exit</span>
        <b>
          {formatClosedWhen(item.lpWithdrawnAt ?? null)} → {formatClosedWhen(item.closedVerifiedAt)}
        </b>
        <small>Full cycle {durationLabel(item.durationMs)}</small>
      </section>
      <section>
        <span>Exit rule</span>
        <b>{settingSummary(item, shownExit)}</b>
      </section>
      <section>
        <span>Price data</span>
        <b className={quality === "historical-estimate" ? "warning" : quality ? "" : "unknown"}>
          {quality === "near-close"
            ? "Observed near withdrawal"
            : quality === "historical-estimate"
              ? "Minute estimate"
              : "—"}
        </b>
      </section>
    </footer>
  );
}

export function ClosedPositionCard({
  item,
  expanded,
  onToggleDetails,
  details,
}: {
  item: ClosedPosition;
  expanded: boolean;
  onToggleDetails: () => void;
  details: ReactNode;
}) {
  const setup = item.openingSetup;
  // One frame for the whole card: the header, both range scales, the footer and every exit/close
  // summary below read display sides/prices out of this single conversion.
  const frame = priceFrame(item.tokenA, item.tokenB);
  const range = geometry(item, frame);
  const shownExit = item.exitSettings
    ? frame.policy(item.exitSettings)
    : setup
      ? frame.fromSetup(setup).initialExitPolicy
      : null;
  const exitIsOriginal = !item.exitSettings && Boolean(setup?.initialExitPolicy);
  const detailsId = `details-${item.positionId}`;
  const [scrubPrice, setScrubPrice] = useState<string | null>(null);
  return (
    <article className="position-card closed-position-card compact-position-card">
      <header className="position-card-header closed-position-header">
        <TokenPair frame={frame} />
        <div className="position-identity-copy">
          <div className="tag-line">
            <h2>
              <TokenPairName frame={frame} />
            </h2>
            <span className="tag purple num">{displayPercent(item.feePercent)}</span>
            <span
              className={`tag lifecycle ${item.closeReason.origin === "automatic" ? "amber" : "muted"}`}
            >
              <span className="status-dot" />
              {lifecycleLabel(item, frame)}
              <span className="closed-mobile-duration">
                {" "}
                · {durationLabel(item.lpDurationMs ?? null)}
              </span>
            </span>
            <DexBadge dex={item.dex} />
          </div>
          <div className="position-meta">
            <span className="num" title={item.positionId}>
              {shorten(item.positionId, 7)}
            </span>
            <span aria-hidden="true">·</span>
            <a
              className="position-pool-link"
              href={dexPoolUrl(item.dex, item.poolAddress)}
              target="_blank"
              rel="noreferrer"
            >
              {dexName(item.dex)} pool
              <svg viewBox="0 0 12 12" aria-hidden="true">
                <path d="M4.5 2.5h5v5" />
                <path d="M9.5 2.5 3 9" />
              </svg>
            </a>
            <span aria-hidden="true">·</span>
            <ClosedWhen item={item} />
            {[item.tokenA, item.tokenB].some((token) => token.usesScaledUiAmount === true) && (
              <span className="scaled-pool-units-compact">
                Pool units (unscaled) · wallet display may differ
              </span>
            )}
          </div>
        </div>
        <button
          className="icon-button expand-position"
          aria-label={expanded ? "Collapse position" : "Expand position"}
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={onToggleDetails}
        >
          <svg viewBox="0 0 14 14" aria-hidden="true">
            <path d="m3.5 5.5 3.5 3.5 3.5-3.5" />
          </svg>
        </button>
      </header>
      <div className="compact-metrics" aria-label="Closed position metrics">
        <CompactResult item={item} />
        <section>
          <small>APR</small>
          <b className={`num ${item.performance?.shortPeriod ? "warning" : ""}`}>
            {displayPercent(item.performance?.simpleAnnualizedReturnPercent ?? null)}
          </b>
          <span>
            {item.performance?.shortPeriod
              ? "indicative · short period"
              : item.performance?.simpleAnnualizedReturnPercent === null || !item.performance
                ? compactUnavailableLabel(item, "rate unavailable")
                : "simple annualization"}
          </span>
        </section>
        <section>
          <small>USD value</small>
          <b className="num">
            {displayMoney(item.performance?.fundingCashResult?.usd.returned ?? null)}
          </b>
          <span>
            {item.performance?.fundingCashResult?.usd.spent == null
              ? "spend unavailable"
              : `from ${displayMoney(item.performance.fundingCashResult.usd.spent)}`}
          </span>
        </section>
        <section>
          <small>Time in LP</small>
          <b className="num">{durationLabel(item.lpDurationMs ?? null)}</b>
        </section>
        <section>
          <small>Range · near withdrawal</small>
          {range ? (
            <>
              <PriceScale
                mini
                closed
                min={range.min}
                max={range.max}
                current={range.marker}
                lowerStop={range.lowerStop}
                upperStop={range.upperStop}
                // Same source order as the active card: the server rule frame, which an inverted
                // opening still has, then the setup's reference price `geometry()` re-oriented.
                entry={frame.rangeFrame(item.rangeMarks ?? null, false)?.entryPrice ?? range.entry}
                // The scrubber reports a canonical price (the price-history series' own
                // base/quote = `executedRange` = canonical); this mini scale is always in the
                // rule frame, so it converts the same way `geometry()` converted `range` above.
                scrubPrice={frame.priceOrNull(scrubPrice)}
                unit={range.unit}
              />
              <PositionPriceScrubber item={item} onScrubPriceChange={setScrubPrice} />
            </>
          ) : (
            <>
              <b>—</b>
              <span>range unavailable</span>
            </>
          )}
        </section>
        <section>
          <small>Exit</small>
          <b>
            {item.closeReason.origin === "automatic"
              ? item.closeReason.rule === "out-of-range"
                ? "Automatic exit · out of range"
                : item.closeReason.rule === "low-yield"
                  ? "Automatic exit · low fee yield"
                  : "Automatic stop"
              : "Manual close"}
          </b>
          <span>
            {shownExit ? `→ ${tokenSymbol(item, shownExit.targetMint)}` : "settings unavailable"}
          </span>
        </section>
      </div>
      {expanded && (
        <>
          <div
            className="position-hero closed-position-hero"
            aria-label="Closed position result summary"
          >
            <NativeResult item={item} />
            <AprResult item={item} />
            <UsdResult item={item} />
          </div>
          <ReportStatus item={item} />
          <div className="closed-result-grid">
            <ResultComposition item={item} />
            <ClosedRange item={item} range={range} scrubPrice={scrubPrice} pairFrame={frame} />
          </div>
          <ClosedFooter item={item} frame={frame} />
        </>
      )}
      {expanded && (
        <section className="closed-audit-details" id={detailsId} aria-label="Audit details">
          <div className="closed-mobile-audit-footer">
            <ClosedFooter item={item} frame={frame} />
          </div>
          <div className="closed-card-audit">
            <FinancialResults item={item} />
            <div className="closed-supporting-context" aria-label="Secondary cash context">
              <SupportingAmount
                label="Requested budget"
                amount={item.requestedFunding}
                item={item}
              />
              <SupportingAmount
                label="Opening expense reserve"
                amount={item.openingExpenseReserve}
                item={item}
                note="Budget context; not the actual spend metric"
              />
              <SupportingAmount
                label="Retained refundable deposit"
                amount={item.retainedRefundableRent}
                item={item}
                note="Kept separate from returned cash and cash result"
              />
            </div>
            <div className="closed-position-facts">
              <section>
                <h3>Opening / LP end / Final exit</h3>
                <dl>
                  <div>
                    <dt>Opened</dt>
                    <dd>{formatClosedWhen(item.openedVerifiedAt)}</dd>
                  </div>
                  <div>
                    <dt>LP ended</dt>
                    <dd>{formatClosedWhen(item.lpWithdrawnAt ?? null)}</dd>
                  </div>
                  <div>
                    <dt>Final exit completed</dt>
                    <dd>{formatClosedWhen(item.closedVerifiedAt)}</dd>
                  </div>
                </dl>
              </section>
              <section>
                <h3>Opening settings</h3>
                {setup ? (
                  (() => {
                    // SETUP-frame source: `shown` is already re-expressed in the display frame,
                    // never a canonical conversion of setup values (the double-inversion bug).
                    const shown = frame.fromSetup(setup);
                    const sameFrameAsSetup = setup.inverted === frame.inverted;
                    return (
                      <dl>
                        <div>
                          <dt>Configured range</dt>
                          <dd>
                            {formatPrice(shown.lowerPrice, "long", [shown.upperPrice])} –{" "}
                            {formatPrice(shown.upperPrice, "long", [shown.lowerPrice])} {frame.unit}
                          </dd>
                        </div>
                        <div>
                          <dt>Width / offsets</dt>
                          <dd>
                            {sameFrameAsSetup ? (
                              <>
                                {formatPercent(setup.widthPercent)} · below{" "}
                                {formatPercent(setup.belowPercent)} · above{" "}
                                {formatPercent(setup.abovePercent)}
                              </>
                            ) : (
                              "— · offsets — (entered in the other quote)"
                            )}
                          </dd>
                        </div>
                      </dl>
                    );
                  })()
                ) : (
                  <p>Opening settings unavailable</p>
                )}
              </section>
              <section>
                <h3>Exit settings</h3>
                <p>{settingSummary(item, shownExit)}</p>
                {exitIsOriginal && (
                  <small>Original opening settings; close-time settings unavailable</small>
                )}
                {shownExit?.enabled && (
                  <small>
                    Lower{" "}
                    {shownExit.lowerTrigger
                      ? formatPrice(shownExit.lowerTrigger, "long")
                      : "not set"}{" "}
                    · Upper{" "}
                    {shownExit.upperTrigger
                      ? formatPrice(shownExit.upperTrigger, "long")
                      : "not set"}
                  </small>
                )}
              </section>
            </div>
          </div>
          {details}
        </section>
      )}
    </article>
  );
}
